import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, track, formatErr } from "@/lib/api";
import { loginWithGoogle } from "@/lib/auth";

const SCALE_LABELS = ["Nggak banget", "Kurang", "Netral", "Setuju", "Banget"];
const LOADING_STEPS = [
  "Membaca pola jawaban lo...",
  "Nyari blind spot...",
  "Nyambungin pola kerja & kehidupan...",
  "Nentuin karakter lo...",
  "Ruang Kendali siap.",
];
const STORE_KEY = "rk_session";

const CAT_TITLE = { kerja: "KERJA", uang: "UANG", relasi: "RELASI", diri: "DIRI SENDIRI", keputusan: "KEPUTUSAN" };

export default function Questionnaire() {
  const { category } = useParams();
  const navigate = useNavigate();

  const [stage, setStage] = useState("intro"); // intro | quiz | processing
  const [sessionId, setSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [loadStep, setLoadStep] = useState(0);
  const advanceTimer = useRef(null);

  // resume incomplete session
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (s.category === category && s.session_id && s.questions?.length) {
          setSessionId(s.session_id);
          setQuestions(s.questions);
          setAnswers(s.answers || {});
          setIndex(s.index || 0);
          setStage("quiz");
          toast("Lanjutin dari sebelumnya ya 👌", { description: `Pertanyaan ${(s.index || 0) + 1}` });
        }
      }
    } catch (e) { /* ignore */ }
  }, [category]);

  const persist = useCallback((sid, qs, ans, idx) => {
    localStorage.setItem(STORE_KEY, JSON.stringify({ category, session_id: sid, questions: qs, answers: ans, index: idx }));
  }, [category]);

  const startSession = async () => {
    try {
      const { data } = await api.post("/sessions", { category });
      setSessionId(data.session_id);
      setQuestions(data.questions);
      setAnswers({});
      setIndex(0);
      setStage("quiz");
      persist(data.session_id, data.questions, {}, 0);
      track("questionnaire_started", data.session_id, { category });
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) { loginWithGoogle(); return; }
      if (status === 403) {
        toast.error(formatErr(e.response?.data?.detail));
        navigate("/pilih");
        return;
      }
      toast.error(formatErr(e.response?.data?.detail) || "Gagal mulai. Coba lagi ya.");
    }
  };

  const total = questions.length;
  const q = questions[index];

  const goNext = (newAnswers) => {
    const ans = newAnswers || answers;
    if (index + 1 >= total) {
      submit(ans);
    } else {
      const ni = index + 1;
      setIndex(ni);
      persist(sessionId, questions, ans, ni);
    }
  };

  const goBack = () => {
    if (index > 0) {
      const ni = index - 1;
      setIndex(ni);
      persist(sessionId, questions, answers, ni);
    }
  };

  const setAnswer = (qid, value, autoAdvance) => {
    const na = { ...answers, [qid]: value };
    setAnswers(na);
    persist(sessionId, questions, na, index);
    if (autoAdvance) {
      clearTimeout(advanceTimer.current);
      advanceTimer.current = setTimeout(() => goNext(na), 320);
    }
  };

  const submit = async (finalAnswers) => {
    setStage("processing");
    track("questionnaire_completed", sessionId, { category });
    // rotate loading messages
    let step = 0;
    const iv = setInterval(() => {
      step = Math.min(step + 1, LOADING_STEPS.length - 1);
      setLoadStep(step);
    }, 1100);
    try {
      const payload = { answers: Object.entries(finalAnswers).map(([question_id, answer]) => ({ question_id, answer })) };
      const { data } = await api.post(`/sessions/${sessionId}/submit`, payload);
      clearInterval(iv);
      setLoadStep(LOADING_STEPS.length - 1);
      localStorage.removeItem(STORE_KEY);
      setTimeout(() => navigate(`/hasil/${data.public_id}`), 700);
    } catch (e) {
      clearInterval(iv);
      toast.error("Gagal memproses. Coba lagi ya.");
      setStage("quiz");
    }
  };

  // ---------- INTRO ----------
  if (stage === "intro") {
    return (
      <div className="relative min-h-screen w-full flex items-center">
        <div className="relative z-10 max-w-2xl mx-auto px-5 sm:px-8 py-16 w-full">
          <button onClick={() => navigate("/pilih")} data-testid="back-button"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-amber-200 transition-colors mb-10">
            <ArrowLeft size={16} /> Balik
          </button>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-400/80 mb-4">
            Analisis · {CAT_TITLE[category] || category}
          </p>
          <h1 className="font-display text-4xl sm:text-6xl font-semibold leading-tight">
            Siap masuk <span className="gold-text">Ruang Kendali?</span>
          </h1>
          <p className="mt-5 text-gray-300 leading-relaxed">
            Bakal ada <span className="text-amber-200">beberapa pertanyaan singkat</span>. Jawab jujur aja,
            nggak ada jawaban benar atau salah. Ini bukan ujian — anggap aja lagi ngobrol sama diri sendiri.
          </p>
          <div className="mt-6 card-surface rounded-2xl p-5 text-sm text-gray-400">
            <p className="mb-2 text-amber-100/80 font-medium flex items-center gap-2"><Sparkles size={15} /> Contoh pertanyaan</p>
            <p className="italic">"Kalau kerjaan lagi kacau, apa yang biasanya lo lakuin?"</p>
          </div>
          <button onClick={startSession} data-testid="start-quiz-button"
            className="mt-8 w-full sm:w-auto gold-btn rounded-full px-8 py-4 inline-flex items-center justify-center gap-3">
            Mulai sekarang <ArrowRight size={18} />
          </button>
          <p className="mt-4 text-xs text-gray-500">Hasil = refleksi dari jawaban lo. Bukan diagnosis, bukan klaim ilmiah.</p>
        </div>
      </div>
    );
  }

  // ---------- PROCESSING ----------
  if (stage === "processing") {
    return (
      <div className="relative min-h-screen w-full flex items-center justify-center">
        <div className="relative z-10 text-center px-6">
          <div className="mx-auto w-20 h-20 rounded-full border-2 border-amber-400/30 border-t-amber-300 animate-spin mb-10" />
          <AnimatePresence mode="wait">
            <motion.p key={loadStep}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="font-display text-2xl sm:text-3xl gold-text">
              {LOADING_STEPS[loadStep]}
            </motion.p>
          </AnimatePresence>
          <p className="mt-4 text-sm text-gray-500 font-mono">Ruang Kendali lagi baca pola lo...</p>
        </div>
      </div>
    );
  }

  // ---------- QUIZ ----------
  if (!q) return null;
  const progress = ((index + 1) / total) * 100;
  const current = answers[q.id];

  return (
    <div className="relative min-h-screen w-full">
      <div className="relative z-10 max-w-2xl mx-auto px-5 sm:px-8 pt-8 pb-24">
        {/* progress */}
        <div className="flex items-center justify-between mb-2">
          <button onClick={goBack} disabled={index === 0} data-testid="prev-question-button"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-amber-200 disabled:opacity-30 transition-colors">
            <ArrowLeft size={15} /> Balik
          </button>
          <span className="font-mono text-xs text-amber-300/80" data-testid="progress-counter">
            Pertanyaan {index + 1} dari {total}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-white/8 overflow-hidden mb-10">
          <motion.div className="h-full rounded-full bg-gradient-to-r from-amber-300 to-amber-500"
            animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={q.id}
            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.35 }}>
            <h2 className="font-display text-3xl sm:text-4xl font-medium leading-snug mb-8" data-testid="question-text">
              {q.question}
            </h2>

            {(q.question_type === "multiple_choice" || q.question_type === "situational") && (
              <div className="space-y-3">
                {q.options.map((o, i) => {
                  const selected = current === o.key;
                  return (
                    <motion.button key={o.key}
                      data-testid={`option-${o.key}`}
                      onClick={() => setAnswer(q.id, o.key, true)}
                      whileTap={{ scale: 0.98 }}
                      className={`w-full text-left rounded-2xl p-4 sm:p-5 border transition-all duration-200 flex items-center gap-4 ${
                        selected ? "border-amber-400 bg-amber-400/10" : "card-surface hover:border-amber-400/40"
                      }`}>
                      <span className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-mono text-sm border ${
                        selected ? "bg-amber-400 text-slate-900 border-amber-400" : "border-white/15 text-gray-400"
                      }`}>{String.fromCharCode(65 + i)}</span>
                      <span className="text-sm sm:text-base text-gray-200">{o.label}</span>
                    </motion.button>
                  );
                })}
              </div>
            )}

            {q.question_type === "scale" && (
              <div>
                <div className="grid grid-cols-5 gap-2 sm:gap-3">
                  {[1, 2, 3, 4, 5].map((v) => {
                    const selected = current === v;
                    return (
                      <button key={v}
                        data-testid={`scale-${v}`}
                        onClick={() => setAnswer(q.id, v, true)}
                        className={`aspect-square rounded-2xl border flex items-center justify-center font-mono text-lg transition-all duration-200 ${
                          selected ? "border-amber-400 bg-amber-400 text-slate-900 scale-105 shadow-[0_0_24px_rgba(229,193,88,0.4)]"
                            : "card-surface hover:border-amber-400/50 text-gray-300"
                        }`}>{v}</button>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-3 text-xs text-gray-500">
                  <span>{SCALE_LABELS[0]}</span>
                  <span>{SCALE_LABELS[4]}</span>
                </div>
              </div>
            )}

            {q.question_type === "ranking" && (
              <RankingInput
                options={q.options}
                value={current}
                onChange={(order) => setAnswer(q.id, order, false)}
                onNext={() => goNext()}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function RankingInput({ options, value, onChange, onNext }) {
  const order = Array.isArray(value) ? value : [];
  const remaining = options.filter((o) => !order.includes(o.key));

  const pick = (key) => onChange([...order, key]);
  const reset = () => onChange([]);

  return (
    <div>
      <p className="text-sm text-gray-400 mb-4">Tap dari yang <span className="text-amber-200">paling penting</span> ke yang paling nggak.</p>
      {order.length > 0 && (
        <div className="space-y-2 mb-5">
          {order.map((key, i) => {
            const o = options.find((x) => x.key === key);
            return (
              <div key={key} className="flex items-center gap-3 rounded-xl border border-amber-400/40 bg-amber-400/10 p-3">
                <span className="w-7 h-7 rounded-lg bg-amber-400 text-slate-900 font-mono text-sm flex items-center justify-center">{i + 1}</span>
                <span className="text-sm text-gray-100">{o?.label}</span>
              </div>
            );
          })}
        </div>
      )}
      <div className="space-y-2">
        {remaining.map((o) => (
          <button key={o.key} data-testid={`rank-option-${o.key}`}
            onClick={() => pick(o.key)}
            className="w-full text-left rounded-xl card-surface hover:border-amber-400/40 border p-3 text-sm text-gray-200 transition-colors">
            {o.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-6">
        <button onClick={reset} data-testid="rank-reset-button"
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-amber-200 transition-colors">
          <RotateCcw size={15} /> Ulang
        </button>
        <button onClick={onNext} disabled={remaining.length > 0} data-testid="rank-next-button"
          className="ml-auto gold-btn rounded-full px-6 py-3 inline-flex items-center gap-2 disabled:opacity-40">
          <Check size={16} /> Lanjut
        </button>
      </div>
    </div>
  );
}
